'use client';

import { FC, Ref, useLayoutEffect, useRef } from 'react';
import {
  RadioGroup as RACRadioGroup,
  RadioGroupProps,
} from 'react-aria-components';

/**
 * Wrapper around `react-aria-components`' RadioGroup that enforces the
 * WAI-ARIA APG roving tabindex pattern.
 *
 * ## Problem
 * react-aria-components@1.8.0 (`@react-aria/radio@3.12.1`) has a bug: when a
 * RadioGroup has no selected value AND hasn't been focused yet, `useRadio`
 * sets `tabIndex=0` on **every** radio button. Per the WAI-ARIA Radio Group
 * pattern (https://www.w3.org/WAI/ARIA/apg/patterns/radio/), only the first
 * enabled radio should be in the tab order (`tabIndex=0`); the rest must be
 * `tabIndex=-1` so that Tab moves focus *out* of the group and arrow keys
 * navigate *within* it.
 *
 * ## Approach
 * We use `useLayoutEffect` (no dependency array — intentionally runs every
 * render) to patch the DOM after RAC commits. Running every render is
 * necessary because RAC re-applies its own tabIndex values on each render,
 * so a one-time fix would be overwritten.
 *
 * When a radio is already checked, RAC handles tabIndex correctly and this
 * wrapper is a no-op.
 *
 * ## Removal
 * This is a temporary workaround. Remove this wrapper once the upstream bug
 * is fixed in react-aria-components — verify by checking that an uncontrolled
 * RadioGroup (no default value) renders only one radio with `tabIndex=0`.
 */
export interface RovingFocusRadioGroupProps extends RadioGroupProps {
  ref?: Ref<HTMLDivElement>;
}

export const RovingFocusRadioGroup: FC<RovingFocusRadioGroupProps> = ({
  ref,
  ...props
}) => {
  const internalRef = useRef<HTMLDivElement>(null);

  const setRefs = (node: HTMLDivElement | null): void => {
    internalRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  useLayoutEffect(() => {
    const root = internalRef.current;
    if (!root) {
      return;
    }

    const radios = Array.from(
      root.querySelectorAll<HTMLInputElement>('input[type="radio"]'),
    );
    if (radios.length === 0) {
      return;
    }

    // When a radio is checked, RAC correctly assigns tabIndex=0 to the
    // checked radio and -1 to the rest — no patching needed.
    const hasChecked = radios.some((r) => r.checked);
    if (hasChecked) {
      return;
    }

    // Nothing is checked — enforce roving tabindex: first enabled radio
    // gets tabIndex=0 (reachable via Tab), all others get -1 (reachable
    // only via arrow keys). Also set disabled radios to -1 for consistency,
    // even though browsers ignore tabIndex on disabled inputs.
    let firstEnabledFound = false;
    radios.forEach((radio) => {
      if (!radio.disabled && !firstEnabledFound) {
        firstEnabledFound = true;
        if (radio.tabIndex !== 0) {
          radio.tabIndex = 0;
        }
      } else {
        if (radio.tabIndex !== -1) {
          radio.tabIndex = -1;
        }
      }
    });
  });

  return <RACRadioGroup ref={setRefs} {...props} />;
};
